import { Modal } from "react-bootstrap";
import styles from "./modal.module.css";
import React from "react";
import { useSelector } from "react-redux";
import { AppState } from "../../store/root.reducer";
import { ApiClient } from "../../utils/apiClient";
import { getA9ErrorMessage, isA9Success } from "../../utils/a9-envelope";

const BASE_API = process.env.REACT_APP_BASE_API;

interface ModalProps {
    show: boolean;
    onHide: () => void;
    onSent: (status: boolean, msg?: string) => void;
}

export const ContactModal = ({ show, onHide, onSent }: ModalProps) => {
    const apiClient = ApiClient(BASE_API);
    const user = useSelector((state: AppState) => state.auth.user);

    const [name, setName] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [message, setMessage] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (show && user) {
            setName(user.name || "");
            setEmail(user.email || "");
        }
        if (!show) {
            setName("");
            setEmail("");
            setMessage("");
            setError(null);
            setLoading(false);
        }
    }, [show, user]);

    const onSubmit = async () => {
        if (!name.trim() || !email.trim() || !message.trim()) {
            setError("Please fill in all fields.");
            return;
        }

        setLoading(true);
        setError(null);

        const resp = await apiClient.sendContactUs(name.trim(), email.trim(), message.trim());
        setLoading(false);

        if (isA9Success(resp)) {
            onSent(true, resp.data?.message);
            onHide();
        } else {
            const errorMessage = getA9ErrorMessage(resp, "Failed to send message");
            setError(errorMessage);
            onSent(false, errorMessage);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            backdrop="static"
        >
            <Modal.Header closeButton className={styles.background}>
                <Modal.Title id="contained-modal-title-vcenter">
                    CONTACT US
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.background}>
                <div className="p-3">
                    <div className="mb-3">
                        <label htmlFor="contactName" className="form-label">Name</label>
                        <input
                            id="contactName"
                            className="form-control"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Your name"
                            autoFocus
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="contactEmail" className="form-label">Email</label>
                        <input
                            id="contactEmail"
                            className="form-control"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="contactMessage" className="form-label">Message</label>
                        <textarea
                            id="contactMessage"
                            className="form-control"
                            rows={8}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="How can we help?"
                        />
                    </div>
                    {error && (
                        <div className="text-danger small mb-2">{error}</div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className={styles.background}>
                <button className="btn btn-success" onClick={onHide} disabled={loading}>
                    Close
                </button>
                <button
                    className="btn btn-success"
                    onClick={onSubmit}
                    disabled={loading || !name.trim() || !email.trim() || !message.trim()}
                >
                    {loading ? "Sending..." : "Send"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};
